import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MultiSelectDropdown from '../MultiSelectComponent/MultiSelectPrimary/selectDropDownComponent';
import { PAGE, SingleOption } from '../licenseAnalyticsSupport/typeInterfaces';
import { useTranslation } from 'react-i18next';
import {
  getRoiReturnValue,
  getRoiInvestmentValue,
  getRoiInvestmentSecondValue,
  getROIData,
  getColorsList,
} from '../licenseAnalyticsSupport/getLicenseAnalyticsActions';
import './GraphComponent.css';
import { shortenNumber } from '../licenseAnalyticsSupport/calculationsUtils';
import {
  OPTIONSENUM,
  getROIFirstLevelOptions,
  getROIReturnOptions,
  getROISecondLevelOptions,
} from '../licenseAnalyticsSupport/filtersOptions';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { setRoiReturnValue, setRoiSecondValue, setRoiValue } from '../../../redux/reducers/licenceAnalytics';

// ✅ FIX 1: Register required Chart.js components for v3+
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const ROIComponent: React.FC<any> = ({ page, departments }: any) => {
  const [t] = useTranslation();
  const colors = useSelector(getColorsList);
  const roiOptions = getROIFirstLevelOptions(page);
  const roiValue: any = useSelector(getRoiInvestmentValue);

  const roiSecondOptions = [
    ...getROISecondLevelOptions(page, roiValue?.id),
    ...(page === PAGE.STAFF &&
    departments.length > 0 &&
    (roiValue?.id == OPTIONSENUM.TOTALSTAFF || roiValue?.id == OPTIONSENUM.AVERAGESALARY)
      ? departments
      : []),
  ];
  const roiSecondValue = useSelector(getRoiInvestmentSecondValue);
  const roiReturnOptions = getROIReturnOptions(page);
  const roiReturnValue = useSelector(getRoiReturnValue);
  const roiSeries = useSelector((state: any) => getROIData(state, t));

  const returnLabel = t(roiReturnValue?.name ?? t('RETURN'));
  const investmentLabel =
    roiSecondValue && page != PAGE.FINANCE
      ? t(roiSecondValue?.name ?? t('INVESTMENT'))
      : t(roiValue?.name ?? t('INVESTMENT'));
  // ✅ FIX 2: Removed the broken useEffect for investmentLabel
  //    (setting a `let` variable inside useEffect has no effect on renders)
  //    Replaced with a derived const above.

  const dispatch = useDispatch();

  const onRoiReturnChange = (newValue: SingleOption | undefined): void => {
    dispatch(setRoiReturnValue({ newValue }));
  };

  const onRoiChange = (newValue: SingleOption | undefined): void => {
    dispatch(setRoiValue({ newValue }));
  };

  const onRoiSecondOptionChange = (newValue: SingleOption | undefined): void => {
    dispatch(setRoiSecondValue({ newValue }));
  };

  const series = roiSeries.map(roi => {
    let color = colors[roi.label] || colors['club'];
    if (roi.label == t('ALL_CLUBS')) {
      color = colors['avg'];
    }
    return { ...roi, backgroundColor: color };
  });

  const data = { datasets: series };

  const option: any = {
    plugins: {
      // ✅ FIX 3: `legend` and `tooltips` move under `plugins` in v3
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 6,
        },
      },
      tooltip: {
        // ✅ FIX 4: `tooltips` is renamed to `tooltip` in v3
        backgroundColor: 'white',
        enabled: true,
        borderColor: '#E5E4E2',
        borderWidth: 1,
        callbacks: {
          labelTextColor: function () {
            return 'black';
          },
          label: function (context: any) {
            // ✅ FIX 5: Tooltip callback signature changed in v3
            const dataPoint = context.raw;
            return (
              dataPoint.club +
              ' [ ' +
              returnLabel +
              ' : ' +
              dataPoint.x +
              ', ' +
              investmentLabel +
              ' : ' +
              (roiValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
                ? dataPoint.y?.toFixed?.(1) + '%'
                : shortenNumber(dataPoint.y)) +
              ']'
            );
          },
        },
      },
    },
    scales: {
      // ✅ FIX 6: `yAxes: [{ ticks: {...} }]` → `y: { ticks: {...} }` in v3
      y: {
        beginAtZero: true,
        ticks: {
          callback: (number: any) => {
            if (roiValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS) {
              return number + '%';
            }
            return shortenNumber(number);
          },
        },
      },
    },
  };

  return (
    <div className="chart" id="chart">
      <div className="title">
        {t('RETURN_ON_INVESTMENT')}
        <h4 style={{ fontWeight: '100' }}>{t('ROI_LABEL')}</h4>
      </div>
      <div className="filtersWrapper">
        <MultiSelectDropdown
          options={roiOptions}
          value={roiValue}
          title={t('INVESTMENT')}
          width="100%"
          rootCss={{ width: '100%', marginRight: '20px' }}
          onChange={onRoiChange}
        />
        {roiSecondOptions.length > 0 ? (
          <MultiSelectDropdown
            options={roiSecondOptions}
            value={roiSecondValue}
            title={t('SELECT')}
            rootCss={{ width: '100%' }}
            onChange={onRoiSecondOptionChange}
            addAll
          />
        ) : (
          <></>
        )}
      </div>
      <div className="vs">vs</div>
      <div className="filtersWrapper">
        {roiReturnOptions && (
          <MultiSelectDropdown
            options={roiReturnOptions}
            value={roiReturnValue}
            title={t('RETURN')}
            width="100%"
            rootCss={{ width: '100%', marginTop: '-15px', marginRight: '20px' }}
            onChange={onRoiReturnChange}
          />
        )}
      </div>
      <Scatter options={option} data={data} height={150} />
    </div>
  );
};

export default ROIComponent;